import React, {useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {formatDate, formatDateTime} from '../utils';
import styled from 'styled-components';
import {Stack} from '@mui/material';
import {StringIsNullOrEmptyOrUndefined} from '../utils/helpers';
import {Chip} from '../components';
import {ColorType} from '../types/typeChip';

export interface ValueFilterInterface {
  [key: string]: any;
}

export interface ParamsInterface {
  [key: string]: any;
}

export interface DateTimeRangeInterface {
  startOfDateTime?: Date | null;
  endOfDateTime?: Date | null;
}

export interface FilterDateRangeConfigInterface {
  filterKeys: string[];
  dateTimeRangPickerState: DateTimeRangeInterface;
  renderChipFormat: string;
  callbackOnDeleteChip: Function;
  handleDeleteChipKey: string;
}

export interface CustomRenderFilterChipLabelInterface {
  [key: string]: Function;
}

export interface CallbacksOnDeleteChipInterface {
  [key: string]: Function;
}

export interface OverwriteGetParamValue {
  [key: string]: Function;
}

interface UsePageFilterInterface {
  defaultValueFilter?: ValueFilterInterface;
  defaultParams?: ParamsInterface;
  customRenderParamsForHandleFilter?: Function;
  filterDateRangeConfig?: FilterDateRangeConfigInterface[];
  rangePickerDateTimeFormat?: string;
  callbackAfterHandleFilter?: Function;
  callbackOnHandleClean?: Function;
  customRenderFilterChipLabel?: CustomRenderFilterChipLabelInterface;
  callbacksOnDeleteChip?: CallbacksOnDeleteChipInterface;
  overwriteGetParamValue?: OverwriteGetParamValue;
}

const StyledStack = styled(Stack)`
  margin-right: 10px;
`;

const usePageFilter = (props: UsePageFilterInterface) => {
  const localRangePickerDateTimeFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
  const {
    defaultValueFilter = {},
    defaultParams = {},
    customRenderParamsForHandleFilter,
    filterDateRangeConfig = [],
    rangePickerDateTimeFormat,
    callbackAfterHandleFilter,
    callbackOnHandleClean,
    callbacksOnDeleteChip = {},
    customRenderFilterChipLabel = {},
    overwriteGetParamValue = {},
  } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const [valueFilter, setValueFilter] =
    useState<ValueFilterInterface>(defaultValueFilter);
  const [params, setParams] = useState<ParamsInterface>({
    ...defaultParams,
    ...Object.keys(defaultValueFilter).reduce(
      (acc, key) => ({
        ...acc,
        [key]: searchParams.get(key) ?? undefined,
      }),
      {} as ParamsInterface
    ),
  });

  /**
   * Handle filter method
   */
  const handleFilter = () => {
    // Get params from value filter
    const paramsFromDefaultValueFilter = Object.keys(valueFilter).reduce(
      (acc: ValueFilterInterface, key: string) => ({
        ...acc,
        [key]: valueFilter[key] || undefined,
      }),
      {} as ValueFilterInterface
    );

    // Get params from date range
    const paramsFromDateRangeConfig = filterDateRangeConfig.reduce(
      (acc: any, config: any) => {
        config.filterKeys.forEach((key: string, index: number) => {
          if (index === 0 && config.dateTimeRangPickerState.startOfDateTime) {
            acc[key] = formatDateTime(
              config.dateTimeRangPickerState.startOfDateTime,
              rangePickerDateTimeFormat || localRangePickerDateTimeFormat
            );
          }
          if (index === 1 && config.dateTimeRangPickerState.endOfDateTime) {
            acc[key] = formatDateTime(
              config.dateTimeRangPickerState.endOfDateTime,
              rangePickerDateTimeFormat || localRangePickerDateTimeFormat
            );
          }
        });
        return acc;
      },
      {} as FilterDateRangeConfigInterface
    );

    // Get custom params
    const customParams = customRenderParamsForHandleFilter?.() || {};

    const updatedParams = {
      ...defaultParams,
      ...paramsFromDefaultValueFilter,
      ...paramsFromDateRangeConfig,
      ...customParams,
    };
    setParams((preState: ParamsInterface) => ({
      ...preState,
      ...updatedParams,
    }));

    // Callback after trigger handle filter
    callbackAfterHandleFilter?.();
  };

  /**
   * Clean filter method
   */
  const handleClean = () => {
    // Trigger custom callback handle clean
    callbackOnHandleClean?.();

    // Loop config for date range and clean all
    filterDateRangeConfig?.map(
      (filterDateRange: FilterDateRangeConfigInterface) =>
        filterDateRange?.callbackOnDeleteChip?.()
    );

    // Clean value filter
    setValueFilter(
      Object.keys(defaultValueFilter).reduce(
        (acc, key) => ({
          ...acc,
          [key]: '',
        }),
        {} as ValueFilterInterface
      )
    );

    // Clean params
    setParams({
      ...defaultParams,
      ...Object.keys(defaultValueFilter).reduce(
        (acc, key) => ({
          ...acc,
          [key]: undefined,
        }),
        {} as ParamsInterface
      ),
    });
  };

  /**
   * Method to get filter params for api
   * @returns Object of params
   */
  const getFilterParams = () => {
    return (
      Object.keys(params)?.reduce((acc: ParamsInterface, key: string) => {
        const baseParamValue = params[key];
        const overwriteValues = overwriteGetParamValue?.[key]?.(baseParamValue);
        if (overwriteValues && Object.keys(overwriteValues)?.length > 0) {
          Object.keys(overwriteValues).map(
            overwriteKey => (acc[overwriteKey] = overwriteValues[overwriteKey])
          );
        } else {
          acc[key] = baseParamValue;
        }
        return acc;
      }, {} as ParamsInterface) || {}
    );
  };

  /**
   * Delete search params based on inputted key
   * @param key Key to check and delete search params
   */
  const deleteSearchParams = (key: string) => {
    if (searchParams.has(key)) {
      searchParams.delete(key);
      setSearchParams(searchParams);
    }
  };

  /**
   * Handle delete chip function
   * @param type Type of chip to determine which chip is being deleted
   */
  const handleDeleteChip = (type: string) => {
    const filterKeys = [type];
    const paramKeys = [type];
    const searchParamKeys = [type];
    const filteredDateRangeConfig = filterDateRangeConfig.find(
      (item: FilterDateRangeConfigInterface) =>
        item.handleDeleteChipKey === type
    );

    // Check and remove filter for date range if exists
    if (filteredDateRangeConfig) {
      filterKeys.push(...filteredDateRangeConfig.filterKeys);
      paramKeys.push(...filteredDateRangeConfig.filterKeys);
      searchParamKeys.push(...filteredDateRangeConfig.filterKeys);
      filteredDateRangeConfig.callbackOnDeleteChip &&
        filteredDateRangeConfig.callbackOnDeleteChip();
    }

    // Remove related search params
    if (searchParamKeys.length > 0) {
      searchParamKeys.forEach((searchParam: string) => {
        deleteSearchParams(searchParam);
      });
    }

    // Remove related filter keys
    if (filterKeys.length > 0) {
      setValueFilter({
        ...valueFilter,
        ...(filterKeys.reduce(
          (acc: ValueFilterInterface, key: string) => ({...acc, [key]: ''}),
          {}
        ) as ValueFilterInterface),
      });
    }

    // Reset related params
    if (paramKeys.length > 0) {
      setParams((preState: ParamsInterface) => ({
        ...preState,
        _page: 0,
        _size: 10,
        ...(paramKeys.reduce(
          (acc: ParamsInterface, key: string) => ({...acc, [key]: undefined}),
          {}
        ) as ParamsInterface),
      }));
    }

    // Trigger other callback on delete chip based on type
    callbacksOnDeleteChip?.[type]?.();
  };

  /**
   * Render filter chips
   * @returns Html for filter chips
   */
  const renderFilterChips = () => {
    return (
      <StyledStack direction="row" spacing={3}>
        {Object.keys(defaultValueFilter).map((key: string) => {
          const searchParamValue = searchParams.get(key);
          const filteredDateRangeConfig = filterDateRangeConfig.find(
            (item: FilterDateRangeConfigInterface) =>
              item.filterKeys?.includes(key)
          );
          if (filteredDateRangeConfig) {
            const filterKeys = filteredDateRangeConfig.filterKeys;
            if (filterKeys[1] === key) {
              return null;
            }
            const startDateTime = searchParams.get(filterKeys[0]);
            const endDateTime = searchParams.get(filterKeys[1]);
            if (
              !StringIsNullOrEmptyOrUndefined(startDateTime) ||
              !StringIsNullOrEmptyOrUndefined(endDateTime)
            ) {
              return (
                <Chip
                  key={key}
                  label={`${
                    startDateTime
                      ? formatDate(
                          startDateTime,
                          filteredDateRangeConfig.renderChipFormat
                        )
                      : '...'
                  } - ${
                    endDateTime
                      ? formatDate(
                          endDateTime,
                          filteredDateRangeConfig.renderChipFormat
                        )
                      : '...'
                  }`}
                  color={'default' as ColorType}
                  onDelete={() =>
                    handleDeleteChip(
                      filteredDateRangeConfig.handleDeleteChipKey as string
                    )
                  }
                />
              );
            }
          }
          const label =
            customRenderFilterChipLabel?.[key]?.(searchParamValue) ||
            searchParamValue;
          return (
            label !== null && (
              <Chip
                label={label as string}
                color={'default' as ColorType}
                onDelete={() => {
                  handleDeleteChip(key);
                }}
              />
            )
          );
        })}
      </StyledStack>
    );
  };

  return {
    valueFilter,
    setValueFilter,
    params,
    setParams,
    handleFilter,
    renderFilterChips,
    handleDeleteChip,
    handleClean,
    deleteSearchParams,
    getFilterParams,
  };
};

export default usePageFilter;
